import { useMemo } from "react";
import PropTypes from "prop-types";
import { DataGridPro, GridPagination, GridToolbarContainer, GridToolbarExport } from "@mui/x-data-grid-pro";
import MuiPagination from "@mui/material/Pagination";
import "./index.scss";
import ProButton from "../ProButton";
import { Box, TextField, useTheme, MenuItem } from "@mui/material";
import "../../GlobalStyles/variables.scss";
import "../../GlobalStyles/mixins.scss";
import GridSerch from "./components/GridSerch";
import { useLocation } from "react-router-dom";

export const getSelectFilterOperator = (options = []) => ({
  label: "is",
  value: "equals",
  getApplyFilterFn: filterItem => {
    if (!filterItem.value) return null;
    return params => {
      const cellValue = params.value === true ? "Yes" : params.value === false ? "No" : params.value;
      return String(cellValue) === String(filterItem.value);
    };
  },
  InputComponent: props => (
    <TextField
      select
      fullWidth
      variant="standard"
      label="Value"
      value={props.item.value || ""}
      onChange={event => props.applyValue({ ...props.item, value: event.target.value })}
      sx={{
        "& .MuiInputBase-root": {
          fontSize: "1rem"
        },
        "& .MuiInputLabel-root": {
          fontSize: "1rem"
        }
      }}
    >
      {options.map(opt => (
        <MenuItem key={opt.value ?? opt} value={opt.value ?? opt}>
          {opt.label ?? opt}
        </MenuItem>
      ))}
    </TextField>
  )
});

function CustomToolbar({
  title = "",
  addButtonLabel = "",
  importExcelLabel = "",
  downloadSampleExcelLabel = "",
  addButtonAction = () => {},
  importExcelAction = () => {},
  downloadSampleExcelAction = () => {},
  hideExport = false,
  hideAddButton = false,
  showImportExcel = false,
  showDownloadSampleExcel = false,
  hideSearch = false,
  searchModel = {},
  additionButtons
}) {
  const location = useLocation();
  const viewingRap = location.pathname.includes("apprentice-180/rap/info");
  const areExtraActionsAvailableForRapInfo = viewingRap && localStorage.getItem("viewingReadOnlyRapInfo") == "false";
  return () => (
    <GridToolbarContainer>
      <div className="proGrid-title-wrapper">
        <div className="proGrid-title">{title}</div>
        {!hideSearch && (
          <div className="proGrid-search-wrapper">
            <GridSerch {...searchModel} />
          </div>
        )}
        {(areExtraActionsAvailableForRapInfo || !viewingRap) && (
          <div className="proGrid-filter">
            {additionButtons}
            {!hideExport && <GridToolbarExport className="export-icon" />}
            {showDownloadSampleExcel && (
              <ProButton variant="outlined" onClick={downloadSampleExcelAction}>
                {downloadSampleExcelLabel}
              </ProButton>
            )}
            {showImportExcel && (
              <ProButton variant="outlined" onClick={importExcelAction}>
                {importExcelLabel}
              </ProButton>
            )}
            {!hideAddButton && <ProButton onClick={addButtonAction}>{addButtonLabel}</ProButton>}
          </div>
        )}
      </div>
    </GridToolbarContainer>
  );
}

function Pagination({ page, onPageChange, className, count, rowsPerPage }) {
  const pageCount = useMemo(() => Math.ceil(count / rowsPerPage), [count, rowsPerPage]);
  const theme = useTheme();
  return (
    <MuiPagination
      color="primary"
      className={className}
      count={pageCount}
      page={page + 1}
      onChange={(event, newPage) => {
        onPageChange(event, newPage - 1);
      }}
      sx={{
        "& .MuiPaginationItem-page": {
          color: theme.palette.progrid.pagination
        },
        "& .MuiPaginationItem-page.Mui-selected": {
          color: "white"
        }
      }}
    />
  );
}

Pagination.propTypes = {
  page: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  className: PropTypes.string,
  count: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired
};

function CustomPagination(props) {
  return <GridPagination ActionsComponent={Pagination} {...props} />;
}

function CustomNoRowsOverlay() {
  return (
    <div className="proGrid-no-rows">
      <Box sx={{ mt: 1 }}>No Rows</Box>
    </div>
  );
}
export default function ProGrid({
  columns = [],
  rows = [],
  loading = false,
  options = {},
  addButtonLabel = "",
  addButtonAction = () => {},
  hideExport = false,
  hideSearch = false,
  title = "",
  searchMode,
  hideAddButton = false,
  showImportExcel = false,
  showDownloadSampleExcel = false,
  importExcelAction = () => {},
  downloadSampleExcelAction = () => {},
  importExcelLabel = "",
  downloadSampleExcelLabel = "",
  hideIfNoData = false,
  searchModel,
  showScrollbar = false,
  additionButtons,
  initialState = {}
}) {
 const proColumns = columns.map(column => ({
  headerClassName: "proGrid-header",
  ...(!column.width && { flex: 1 }),
  ...(column.isDeleteColumn || column.field?.toLowerCase() === "actions"
    ? { sortable: false }
    : {}),
  ...column
}));

  if (hideIfNoData && !rows.length) return null;
  return (
    <div className="rs-grid-wrapper v2" style={{display: "flex", flexDirection: "column", flex: 1, minHeight: 0}}>
      <DataGridPro
        sx={{ minHeight: 0 }}
        columnHeaderHeight={44}
        rowHeight={44}
        columns={proColumns}
        rows={rows}
        loading={loading}
        classes={{
          root: showScrollbar ? "show-scrollbar" : "",
          cell: "proGrid-table-cell"
        }}
        sortingOrder={["desc", "asc"]}
        slots={{
          toolbar: CustomToolbar({
            title,
            addButtonLabel,
            importExcelLabel,
            downloadSampleExcelLabel,
            addButtonAction,
            importExcelAction,
            downloadSampleExcelAction,
            hideExport,
            hideAddButton,
            showImportExcel,
            showDownloadSampleExcel,
            hideSearch,
            searchModel,
            additionButtons
          }),
          pagination: CustomPagination,
          noRowsOverlay: CustomNoRowsOverlay
        }}
        initialState={initialState}
        pagination={rows.length > 0}
        {...options}
      />
    </div>
  );
}

ProGrid.propTypes = {
  columns: PropTypes.array.isRequired,
  rows: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  options: PropTypes.object,
  addButtonLabel: PropTypes.string,
  addButtonAction: PropTypes.func,
  hideExport: PropTypes.bool,
  hideSearch: PropTypes.bool,
  title: PropTypes.string,
  searchMode: PropTypes.bool,
  hideAddButton: PropTypes.bool,
  showImportExcel: PropTypes.bool,
  showDownloadSampleExcel: PropTypes.bool,
  importExcelAction: PropTypes.func,
  downloadSampleExcelAction: PropTypes.func,
  importExcelLabel: PropTypes.string,
  downloadSampleExcelLabel: PropTypes.string,
  hideIfNoData: PropTypes.bool,
  searchModel: PropTypes.object,
  additionButtons: PropTypes.node,
  showScrollbar: PropTypes.bool,
  initialState: PropTypes.object
};
